import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { toast } from 'sonner';
import { fmtEur } from '../../lib/constants';
import { printReceipt, printReceiptSmart } from '../../lib/receipt';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Banknote, CreditCard, Split, Check, Gift, Tag, X, Minus, Plus } from 'lucide-react';
import { cn } from '../../lib/utils';
import { AmountInput } from './NumericKeypad';

export const PaymentDialog = ({ open, onOpenChange, order, onPaid }) => {
  const { t } = useTranslation();
  const [liveOrder, setLiveOrder] = useState(order);
  const [method, setMethod] = useState('cash');
  const [cash, setCash] = useState('');
  const [card, setCard] = useState('');
  const [received, setReceived] = useState(''); // con quanto paga il cliente (contanti)
  const [tip, setTip] = useState('');           // mancia
  const [splitMode, setSplitMode] = useState(false);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);

  // SumUp (pagamento carta con lettore fisico)
  const [sumupWaiting, setSumupWaiting] = useState(false);
  const [sumupTxId, setSumupTxId] = useState(null);
  const [sumupCancelling, setSumupCancelling] = useState(false);
  const sumupManualRef = useRef(null); // 'paid' | 'cancel' | null

  // Codice sconto
  const [discountCode, setDiscountCode] = useState('');
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  // Switch funzioni sconto + elenco sconti prefatti (per selezione rapida).
  const [dSettings, setDSettings] = useState({ codes_enabled: false, presets_enabled: false });
  const [presets, setPresets] = useState([]);

  // Gift card
  const [gcCode, setGcCode] = useState('');
  const [gcCard, setGcCard] = useState(null);
  const [verifyingGc, setVerifyingGc] = useState(false);

  const remaining = useMemo(() => {
    if (!liveOrder) return 0;
    return Math.max(0, Number(liveOrder.total || 0) - Number(liveOrder.paid_amount || 0));
  }, [liveOrder]);

  const splitTotal = useMemo(() => {
    if (!liveOrder) return 0;
    return (liveOrder.items || []).reduce((s, it) => {
      const totQ = Number(it.qty || 0);
      const avail = Math.max(0, totQ - Number(it.paid_qty || 0));
      const q = Math.min(Number(selected[it.id] || 0), avail);
      if (it.paid || q <= 0) return s;
      const unit = Number(it.line_total || 0) / Math.max(1, totQ);
      return s + unit * q;
    }, 0);
  }, [liveOrder, selected]);

  const amount = splitMode ? splitTotal : remaining;
  // Importo effettivamente coperto dal buono regalo (mai oltre il saldo).
  const giftPayable = gcCard ? Math.min(Number(gcCard.balance || 0), amount) : 0;

  // Resto: differenza tra i contanti consegnati e l'importo da pagare.
  // Se il campo "con quanto paga" è vuoto -> pagamento con importo esatto (resto 0).
  const changeDue = useMemo(() => {
    const r = Number(received || 0);
    return r > amount ? Math.max(0, r - amount) : 0;
  }, [received, amount]);

  useEffect(() => {
    if (open) {
      setLiveOrder(order);
      setMethod('cash'); setCash(''); setCard(''); setReceived(''); setTip(''); setSplitMode(false); setSelected({});
      setDiscountCode(''); setGcCode(''); setGcCard('');
      // Carica gli switch sconto e l'elenco sconti prefatti attivi.
      (async () => {
        try {
          const [st, ds] = await Promise.all([api.get('/discounts/settings'), api.get('/discounts')]);
          setDSettings(st.data || { codes_enabled: false, presets_enabled: false });
          setPresets((ds.data || []).filter((d) => d.active !== false));
        } catch (e) {
          setDSettings({ codes_enabled: false, presets_enabled: false });
          setPresets([]);
        }
      })();
    }
  }, [open, order]);

  const refreshOrder = async () => {
    try {
      const { data } = await api.get(`/orders/${order.id}`);
      setLiveOrder(data);
    } catch (e) { /* no-op */ }
  };

  const applyDiscount = async () => {
    const code = discountCode.trim().toUpperCase();
    if (!code) { toast.error(t('payment.errDiscountCode')); return; }
    setApplyingDiscount(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/discount/apply`, { code });
      setLiveOrder(data);
      setDiscountCode('');
      toast.success(t('payment.discountApplied', { amount: fmtEur(data.discount_amount) }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('payment.invalidCode'));
    } finally { setApplyingDiscount(false); }
  };

  const removeDiscount = async () => {
    try {
      const { data } = await api.post(`/orders/${order.id}/discount/remove`);
      setLiveOrder(data);
      toast.success(t('payment.discountRemoved'));
    } catch (e) { toast.error(t('common.error')); }
  };

  // Applica uno sconto prefatto (selezione rapida, senza digitare il codice).
  const applyPreset = async (d) => {
    setApplyingDiscount(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/discount/apply`, { code: d.code });
      setLiveOrder(data);
      toast.success(t('payment.discountApplied', { amount: fmtEur(data.discount_amount) }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('payment.invalidCode'));
    } finally { setApplyingDiscount(false); }
  };

  const presetValueLabel = (d) => (d.type === 'percent' ? `${Number(d.value)}%` : fmtEur(d.value));

  const verifyGiftCard = async () => {
    const code = gcCode.trim().toUpperCase();
    if (!code) { toast.error(t('payment.errGiftCode')); return; }
    setVerifyingGc(true);
    try {
      const { data } = await api.get(`/giftcards/by-code/${code}`);
      if (data.status === 'disabled') { toast.error(t('payment.giftDisabled')); setGcCard(null); return; }
      if (Number(data.balance) <= 0) { toast.error(t('payment.giftNoBalance')); setGcCard(null); return; }
      setGcCard(data);
      toast.success(t('payment.giftValid', { balance: fmtEur(data.balance) }));
    } catch (e) {
      setGcCard(null);
      toast.error(e?.response?.data?.detail || t('payment.giftNotFound'));
    } finally { setVerifyingGc(false); }
  };

  // Avvia il pagamento carta sul lettore SumUp (se configurato e attivo).
  // Ritorna { ok, used }. Se SumUp non e' attivo -> used=false (flusso normale).
  const runSumUpCard = async (payAmount) => {
    let conf = null;
    try { const { data } = await api.get('/sumup/config'); conf = data; } catch (e) { conf = null; }
    if (!conf || !conf.enabled || !conf.reader_id) return { ok: true, used: false };

    setSumupWaiting(true);
    sumupManualRef.current = null;
    try {
      const { data: co } = await api.post('/sumup/checkout', {
        order_id: order.id,
        amount: payAmount,
        description: `Ordine ${order.table_name || ''}`.trim(),
      });
      const txId = co.client_transaction_id;
      setSumupTxId(txId);
      const deadline = Date.now() + 180000; // max 3 minuti
      let lastDiag = null;
      while (Date.now() < deadline) {
        // Controllo azioni manuali del cassiere (ogni ciclo)
        if (sumupManualRef.current === 'paid') { setSumupWaiting(false); return { ok: true, used: true }; }
        if (sumupManualRef.current === 'cancel') { setSumupWaiting(false); return { ok: false, used: true }; }
        await new Promise((r) => setTimeout(r, 2500));
        if (sumupManualRef.current === 'paid') { setSumupWaiting(false); return { ok: true, used: true }; }
        if (sumupManualRef.current === 'cancel') { setSumupWaiting(false); return { ok: false, used: true }; }
        let st = 'PENDING';
        try { const { data: s } = await api.get(`/sumup/checkout/${txId}/status`); st = s.status; lastDiag = s; } catch (e) {}
        if (st === 'SUCCESSFUL') { setSumupWaiting(false); return { ok: true, used: true }; }
        if (st === 'FAILED') { setSumupWaiting(false); toast.error(t('sumup.paymentFailed')); return { ok: false, used: true }; }
      }
      // Scaduto il tempo: non chiudiamo automaticamente; lasciamo decidere al cassiere.
      if (sumupManualRef.current === 'paid') { setSumupWaiting(false); return { ok: true, used: true }; }
      setSumupWaiting(false);
      // Diagnostica visibile: mostra l'ultima risposta di SumUp per capire il problema.
      try {
        const codes = (lastDiag?.debug || []).map((d) => `${(d.path||'').split('/').pop()}:${d.code || d.error}`).join(' | ');
        console.warn('SumUp diagnostica:', lastDiag);
        toast.warning(t('sumup.timeout') + (codes ? ` [${codes}]` : ''), { duration: 12000 });
      } catch (e) { toast.warning(t('sumup.timeout')); }
      return { ok: false, used: true };
    } catch (e) {
      setSumupWaiting(false);
      toast.error(e?.response?.data?.detail || t('sumup.checkoutError'));
      return { ok: false, used: true };
    }
  };

  const cancelSumUp = async () => {
    sumupManualRef.current = 'cancel';
    if (sumupTxId) { try { await api.post(`/sumup/checkout/${sumupTxId}/cancel`); } catch (e) {} }
    setSumupWaiting(false);
  };

  // Rete di sicurezza: il pagamento e' riuscito sul lettore ma il POS non l'ha
  // rilevato automaticamente -> il cassiere forza la chiusura del conto.
  const forceSumUpPaid = () => {
    sumupManualRef.current = 'paid';
    setSumupWaiting(false);
  };

  const confirm = async () => {
    if (amount <= 0) { toast.error(t('payment.invalidAmount')); return; }
    const splitItems = splitMode
      ? Object.entries(selected).map(([id, q]) => ({ item_id: id, qty: Number(q) })).filter((x) => x.qty > 0)
      : null;
    let payload = { order_id: order.id, method, amount, split_items: splitItems, item_ids: null };

    if (method === 'cash') {
      payload.cash = amount;
      // Se il cassiere ha indicato con quanto paga il cliente, lo passiamo per il resto.
      if (received !== '' && received != null && Number(received) > 0) payload.received = Number(received);
    }
    else if (method === 'card') payload.card = amount;
    else if (method === 'giftcard') {
      if (!gcCard) { toast.error(t('payment.verifyGiftFirst')); return; }
      const pay = Math.min(Number(gcCard.balance || 0), amount);
      if (pay <= 0) { toast.error(t('payment.giftInsufficient')); return; }
      payload.method = 'giftcard';
      payload.amount = pay;
      payload.gift_card_code = gcCard.code;
    } else {
      const c = Number(cash || 0), cc = Number(card || 0);
      if (Math.abs((c + cc) - amount) > 0.01) { toast.error(t('payment.mixedMismatch')); return; }
      payload.cash = c; payload.card = cc;
    }
    // Mancia (opzionale) su qualsiasi metodo.
    if (tip !== '' && tip != null && Number(tip) > 0) payload.tip = Number(tip);

    setLoading(true);
    try {
      // Pagamento carta con lettore fisico SumUp (se attivo)
      if (method === 'card') {
        const res = await runSumUpCard(payload.amount);
        if (!res.ok) { setLoading(false); return; }
      }
      const { data } = await api.post('/payments', payload);
      toast.success(data.order_closed ? t('payment.paymentCompleted') : t('payment.partialPayment', { amount: fmtEur(payload.amount) }));
      if (data.order_closed) {
        try {
          const { data: receipt } = await api.get(`/orders/${order.id}/receipt`, { params: { type: 'scontrino' } });
          if (receipt.waiter_printer && receipt.waiter_printer.enabled) {
            // Cameriere: stampa lo scontrino sulla stampante cameriere (non sulla principale).
            await printReceiptSmart(receipt);
            toast.success(t('payment.fiscalReceiptPrinting'));
          } else if (receipt.print_agent_enabled) {
            toast.success(t('payment.receiptSentToPrinter'));
          } else {
            printReceipt(receipt);
            toast.success(t('payment.fiscalReceiptPrinting'));
          }
        } catch (e) {
          toast.error(t('payment.paymentOkReceiptFailed'));
        }
        onPaid && onPaid(data);
        onOpenChange(false);
      } else {
        // Pagamento parziale (es. buono che non copre tutto): aggiorna e resta aperto.
        onPaid && onPaid(data);
        await refreshOrder();
        setSelected({});
        setGcCard(null); setGcCode('');
        setMethod('cash');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('payment.paymentError'));
    } finally {
      setLoading(false);
    }
  };

  if (!order) return null;

  const hasDiscount = liveOrder && Number(liveOrder.discount_amount || 0) > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent noAnimation className="p-0 gap-0 flex flex-col overflow-hidden max-w-none w-screen h-[100dvh] rounded-none left-0 top-0 translate-x-0 translate-y-0 sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:w-[calc(100vw-1rem)] sm:max-w-lg sm:h-auto sm:max-h-[92vh] sm:rounded-lg">
        <DialogHeader className="shrink-0 px-4 sm:px-6 pt-4 sm:pt-6 pb-2"><DialogTitle>{t('payment.title', { table: order.table_name })}</DialogTitle></DialogHeader>
        {sumupWaiting && (
          <div className="absolute inset-0 z-20 bg-background/95 backdrop-blur-sm flex flex-col items-center justify-center gap-4 rounded-2xl" data-testid="sumup-waiting-overlay">
            <div className="h-16 w-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center animate-pulse">
              <CreditCard className="h-8 w-8" />
            </div>
            <div className="text-center px-8">
              <div className="font-display font-bold text-lg">{t('sumup.waitingTitle')}</div>
              <p className="text-sm text-muted-foreground mt-1">{t('sumup.waitingText', { amount: fmtEur(amount) })}</p>
            </div>
            <div className="flex flex-col items-center gap-2 w-full px-8">
              <Button className="rounded-xl w-full max-w-xs" onClick={forceSumUpPaid} data-testid="sumup-force-paid-button">
                <Check className="h-4 w-4 mr-2" /> {t('sumup.forcePaid')}
              </Button>
              <Button variant="ghost" className="rounded-xl" onClick={cancelSumUp} disabled={sumupCancelling} data-testid="sumup-cancel-button">{sumupCancelling ? t('sumup.cancelling') : t('sumup.cancel')}</Button>
            </div>
            <p className="text-xs text-muted-foreground text-center px-10">{t('sumup.forcePaidHint')}</p>
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 sm:px-6 py-2 space-y-4">
          {/* Riepilogo importo */}
          <div className="rounded-xl bg-muted px-4 py-3 space-y-1">
            {hasDiscount && (
              <>
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>{t('payment.subtotal')}</span><span className="tabular">{fmtEur(liveOrder.items_total)}</span>
                </div>
                <div className="flex items-center justify-between text-sm text-pos-free">
                  <span className="flex items-center gap-1"><Tag className="h-3.5 w-3.5" /> {t('payment.discountLabel')} {liveOrder.discount_code}</span>
                  <span className="tabular">- {fmtEur(liveOrder.discount_amount)}</span>
                </div>
              </>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('payment.toPay')}</span>
              <span className="text-2xl font-display font-semibold tabular" data-testid="payment-amount">{fmtEur(amount)}</span>
            </div>
          </div>

          {/* Sconti: mostrati solo se attivata almeno una delle due funzioni */}
          {!splitMode && (dSettings.codes_enabled || dSettings.presets_enabled) && (
            <div className="rounded-xl border border-border p-3" data-testid="discount-section">
              <Label className="text-sm flex items-center gap-1.5 mb-1.5"><Tag className="h-4 w-4 text-primary" /> {t('payment.discountLabel')}</Label>
              {hasDiscount ? (
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono font-semibold">{liveOrder.discount_code}</span>
                  <Button variant="ghost" size="sm" className="rounded-lg text-destructive" onClick={removeDiscount} data-testid="discount-remove-button"><X className="h-4 w-4 mr-1" /> {t('common.remove')}</Button>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {/* Sconti prefatti: selezione rapida a pulsanti */}
                  {dSettings.presets_enabled && presets.length > 0 && (
                    <div className="flex flex-wrap gap-2" data-testid="discount-presets">
                      {presets.map((d) => (
                        <Button
                          key={d.id}
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={applyingDiscount}
                          onClick={() => applyPreset(d)}
                          className="rounded-full"
                          data-testid={`discount-preset-${d.id}`}
                        >
                          {d.code} · {presetValueLabel(d)}
                        </Button>
                      ))}
                    </div>
                  )}
                  {/* Campo codice sconto (digitazione) */}
                  {dSettings.codes_enabled && (
                    <div className="flex gap-2">
                      <Input value={discountCode} onChange={(e) => setDiscountCode(e.target.value.toUpperCase())} placeholder={t('payment.discountCode')} className="font-mono uppercase" data-testid="discount-code-input" />
                      <Button variant="outline" className="rounded-xl shrink-0" onClick={applyDiscount} disabled={applyingDiscount} data-testid="discount-apply-button">{t('payment.apply')}</Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Conto separato */}
          <button onClick={() => setSplitMode((s) => !s)} className={cn('w-full min-h-12 py-2 px-3 rounded-lg border flex items-center justify-center gap-2 text-sm font-medium transition-colors text-center', splitMode ? 'border-primary text-primary bg-primary/5' : 'border-border text-muted-foreground hover:bg-muted')} data-testid="payment-split-toggle">
            <Split className="h-4 w-4 shrink-0" /> <span className="leading-tight">{splitMode ? t('payment.splitActive') : t('payment.splitInactive')}</span>
          </button>

          {splitMode && (
            <div className="max-h-56 overflow-y-auto overflow-x-hidden rounded-lg border border-border divide-y divide-border">
              {(liveOrder.items || []).map((it) => {
                const totQ = Number(it.qty || 0);
                const paidQ = Number(it.paid_qty || 0);
                const avail = Math.max(0, totQ - paidQ);
                if (it.paid || avail <= 0) return null;
                const unit = Number(it.line_total || 0) / Math.max(1, totQ);
                const sel = Math.min(Number(selected[it.id] || 0), avail);
                const setQ = (q) => setSelected((s) => ({ ...s, [it.id]: Math.max(0, Math.min(avail, q)) }));
                return (
                  <div key={it.id} className="flex items-center justify-between gap-2 px-2.5 py-2 text-sm" data-testid={`split-line-${it.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{it.name}{it.variant ? ` (${it.variant})` : ''}</div>
                      <div className="text-xs text-muted-foreground truncate">{fmtEur(unit)} × {avail} {t('payment.splitAvailable', { defaultValue: 'disp.' })}{paidQ > 0 ? ` · ${paidQ} ${t('payment.splitPaid', { defaultValue: 'pagati' })}` : ''}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={sel <= 0} onClick={() => setQ(sel - 1)} data-testid={`split-minus-${it.id}`}><Minus className="h-4 w-4" /></Button>
                      <span className="w-6 text-center font-semibold tabular" data-testid={`split-qty-${it.id}`}>{sel}</span>
                      <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={sel >= avail} onClick={() => setQ(sel + 1)} data-testid={`split-plus-${it.id}`}><Plus className="h-4 w-4" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Metodo di pagamento */}
          <Tabs value={method} onValueChange={setMethod}>
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="cash" data-testid="pay-method-cash"><Banknote className="h-4 w-4 mr-1" /> {t('payment.cash')}</TabsTrigger>
              <TabsTrigger value="card" data-testid="pay-method-card"><CreditCard className="h-4 w-4 mr-1" /> {t('payment.card')}</TabsTrigger>
              <TabsTrigger value="mixed" data-testid="pay-method-mixed">{t('payment.mixed')}</TabsTrigger>
              <TabsTrigger value="giftcard" data-testid="pay-method-giftcard"><Gift className="h-4 w-4 mr-1" /> {t('payment.giftcard')}</TabsTrigger>
            </TabsList>
          </Tabs>

          {method === 'cash' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('payment.cashReceived', { defaultValue: 'Con quanto paga' })}</Label>
                <AmountInput value={received} onValueChange={setReceived} label={t('payment.cashReceived', { defaultValue: 'Con quanto paga' })} className="mt-1.5" placeholder={fmtEur(amount)} data-testid="pay-received-input" />
              </div>
              <div>
                <Label>{t('payment.change', { defaultValue: 'Resto' })}</Label>
                <div className="mt-1.5 h-10 rounded-md border border-border bg-muted flex items-center justify-end px-3 text-lg font-display font-semibold tabular text-primary" data-testid="payment-change">{fmtEur(changeDue)}</div>
              </div>
            </div>
          )}

          {(method === 'cash' || method === 'card') && (
            <div>
              <Label className="flex items-center gap-1.5"><Gift className="h-4 w-4 text-primary" /> {t('payment.tip', { defaultValue: 'Mancia' })}</Label>
              <AmountInput value={tip} onValueChange={setTip} label={t('payment.tip', { defaultValue: 'Mancia' })} className="mt-1.5" placeholder="0.00" data-testid="pay-tip-input" />
            </div>
          )}

          {method === 'mixed' && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t('payment.cash')}</Label><AmountInput value={cash} onValueChange={setCash} label={t('payment.cash')} className="mt-1.5" placeholder="0.00" data-testid="pay-cash-input" /></div>
              <div><Label>{t('payment.card')}</Label><AmountInput value={card} onValueChange={setCard} label={t('payment.card')} className="mt-1.5" placeholder="0.00" data-testid="pay-card-input" /></div>
            </div>
          )}

          {method === 'giftcard' && (
            <div className="rounded-xl border border-border p-3 space-y-2">
              <Label className="text-sm">{t('payment.giftCardCode')}</Label>
              <div className="flex gap-2">
                <Input value={gcCode} onChange={(e) => { setGcCode(e.target.value.toUpperCase()); setGcCard(null); }} placeholder={t('payment.scanOrType')} className="font-mono uppercase" data-testid="giftcard-code-input" />
                <Button variant="outline" className="rounded-xl shrink-0" onClick={verifyGiftCard} disabled={verifyingGc} data-testid="giftcard-verify-button">{t('payment.verify')}</Button>
              </div>
              {gcCard && (
                <div className="rounded-lg bg-pos-free/10 border border-pos-free/20 p-2.5 text-sm" data-testid="giftcard-verified-info">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('payment.giftBalance')}</span>
                    <span className="font-semibold tabular">{fmtEur(gcCard.balance)}</span>
                  </div>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="text-muted-foreground">{t('payment.willCharge')}</span>
                    <span className="font-semibold tabular text-pos-free">{fmtEur(giftPayable)}</span>
                  </div>
                  {giftPayable < amount && (
                    <div className="text-xs text-muted-foreground mt-1">{t('payment.remainingToPay', { amount: fmtEur(amount - giftPayable) })}</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-border px-4 sm:px-6 py-3">
          <Button onClick={confirm} disabled={loading} data-testid="payments-confirm-button" className="w-full h-12 rounded-xl text-base">
            <Check className="h-5 w-5 mr-2" /> {loading ? t('payment.processing') : t('payment.collect', { amount: fmtEur(method === 'giftcard' && gcCard ? giftPayable : amount) })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
